/**
 * Tenant Detection Service — ecommerce storefront
 *
 * Resolution order:
 * 1. {slug}.app.busmgr.com / {slug}.app.localhost (sync)
 * 2. Active custom domain via GET /tenants/by-domain/:hostname (async bootstrap)
 * 3. localhost ?tenant= query param (dev)
 *
 * @see docs/FEATURE_SPEC_STOREFRONT_CUSTOM_DOMAINS.md
 */

import { API_URL } from './apiConfig';

const APP_SUBDOMAINS = new Set([
  'www', 'api', 'office', 'pos', 'rpos', 'kds', 'herds', 'onboard',
  'app', 'admin', 'restaurant', 'ecommerce', 'shop', 'store', 'alt',
  'mail', 'smtp', 'ftp', 'cdn', 'static', 'dev', 'staging', 'test', 'demo',
  'kitchen', 'terminal', 'signup', 'backend', 'portal', 'market', 'vendor', 'payroll',
]);

let cachedSlug = null;
let bootstrapPromise = null;

function isLocalHostname(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
}

/** Sync subdomain parse — {slug}.app.busmgr.com and {slug}.app.localhost */
export function trySubdomainTenantSlug(hostname = window.location.hostname) {
  const parts = hostname.split('.');

  // {tenant}.app.{domain...} — "app" is always the second label
  // e.g. hood-family-farms.app.busmgr.com (4 parts) or hood-family-farms.app.localhost (3)
  if (parts.length >= 3 && parts[1] === 'app') {
    const potentialTenant = parts[0].toLowerCase();
    if (!APP_SUBDOMAINS.has(potentialTenant)) {
      return potentialTenant;
    }
  }

  return null;
}

function devTenantSlugFromQuery() {
  const params = new URLSearchParams(window.location.search);
  return params.get('tenant') || null;
}

function sessionCacheKey(hostname) {
  return `bm_storefront_tenant_slug:${hostname}`;
}

/**
 * Bootstrap tenant slug (call once before rendering storefront).
 * @returns {Promise<string|null>} slug or null when host is unmapped
 */
export async function bootstrapTenantSlug() {
  if (cachedSlug) return cachedSlug;
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = (async () => {
    const hostname = window.location.hostname;

    const fromSubdomain = trySubdomainTenantSlug(hostname);
    if (fromSubdomain) {
      cachedSlug = fromSubdomain;
      return cachedSlug;
    }

    if (isLocalHostname(hostname)) {
      cachedSlug = devTenantSlugFromQuery();
      return cachedSlug;
    }

    const cached = sessionStorage.getItem(sessionCacheKey(hostname));
    if (cached) {
      cachedSlug = cached;
      return cachedSlug;
    }

    try {
      const response = await fetch(
        `${API_URL}/tenants/by-domain/${encodeURIComponent(hostname)}`
      );
      if (response.ok) {
        const json = await response.json();
        const slug = json?.data?.slug;
        if (slug) {
          cachedSlug = slug;
          sessionStorage.setItem(sessionCacheKey(hostname), slug);
          return cachedSlug;
        }
      }
    } catch (err) {
      console.warn('bootstrapTenantSlug: by-domain lookup failed', err);
    }

    // Unmapped host — never invent a tenant slug
    cachedSlug = null;
    return null;
  })();

  return bootstrapPromise;
}

/**
 * Current tenant slug (after bootstrap, or sync subdomain on busmgr hosts).
 */
export const getTenantFromSubdomain = () => {
  if (cachedSlug) return cachedSlug;

  const hostname = window.location.hostname;
  const fromSubdomain = trySubdomainTenantSlug(hostname);
  if (fromSubdomain) return fromSubdomain;

  if (isLocalHostname(hostname)) {
    return devTenantSlugFromQuery();
  }

  return cachedSlug;
};

export const getTenantId = () => getTenantFromSubdomain();

export const isDevelopment = () => isLocalHostname(window.location.hostname);

export default {
  bootstrapTenantSlug,
  trySubdomainTenantSlug,
  getTenantFromSubdomain,
  getTenantId,
  isDevelopment,
};
